import { useId, useState } from 'react'
import { Link } from 'react-router'
import {
  type NewsArticle,
  WhatsNew,
  Mtds,
  PencilSimpleIcon,
  TrashIcon,
  EyeIcon
} from 'prove-common'
import { formatNewsDate } from '../../utils/newsDates'
import {
  newsAppLabel,
  newsSummary,
  newsTypeLabel,
  useNewsExpired
} from '../../utils/newsArticle'
import { NewsDeleteDialog } from '../news-delete-dialog/NewsDeleteDialog'

type NewsArticleCardProps = {
  article: NewsArticle
  detailTo: string
  editTo: string
}

const NewsArticleCard = ({
  article,
  detailTo,
  editTo
}: NewsArticleCardProps) => {
  const id = useId()
  const linkId = `${id}-link`
  const menuId = `${id}-menu`
  const [previewOpen, setPreviewOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const summary = newsSummary(article)
  const expired = useNewsExpired(article.endDate)

  return (
    <>
      <Mtds.Card data-pad='4' data-clickdelegatefor={linkId}>
        <Mtds.Flex
          data-align='center'
          data-justify='space-between'
          data-gap='4'
        >
          <Mtds.Grid data-gap='1' className='min-w-0'>
            <Mtds.Heading as='h2' data-size='xs'>
              <Link id={linkId} to={detailTo}>
                {summary}
              </Link>
            </Mtds.Heading>
            <Mtds.Muted>
              {newsAppLabel(article.app)} · {newsTypeLabel(article.icon)} ·
              Siste visningsdag {formatNewsDate(article.endDate)}
            </Mtds.Muted>
          </Mtds.Grid>
          <Mtds.Flex data-align='center' data-fixed data-nowrap>
            <Mtds.Tag data-color={expired ? 'neutral' : 'success'}>
              {expired ? 'Utløpt' : 'Tilgjengelig'}
            </Mtds.Tag>
            <Mtds.Button
              type='button'
              aria-label={`Handlingsmeny for ${summary}`}
              popoverTarget={menuId}
              onClick={(event) => event.stopPropagation()}
            />
            <Mtds.Popover as='menu' id={menuId}>
              <li>
                <Mtds.Button
                  as={Link}
                  to={editTo}
                  aria-label={`Rediger ${summary}`}
                  onClick={(event) => event.stopPropagation()}
                >
                  <PencilSimpleIcon />
                  Rediger
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button
                  type='button'
                  popoverTargetAction='hide'
                  onClick={(event) => {
                    event.stopPropagation()
                    setPreviewOpen(true)
                  }}
                >
                  <EyeIcon />
                  Forhåndsvis
                </Mtds.Button>
              </li>
              <li>
                <Mtds.Button
                  type='button'
                  data-color='danger'
                  popoverTargetAction='hide'
                  onClick={(event) => {
                    event.stopPropagation()
                    setConfirmDelete(true)
                  }}
                >
                  <TrashIcon />
                  Slett artikkel
                </Mtds.Button>
              </li>
            </Mtds.Popover>
          </Mtds.Flex>
        </Mtds.Flex>
      </Mtds.Card>
      <WhatsNew
        article={article}
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
      />
      {confirmDelete && (
        <NewsDeleteDialog
          article={article}
          onClose={() => setConfirmDelete(false)}
        />
      )}
    </>
  )
}

export default NewsArticleCard
